import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { bot, health, lobbies, pool } from '../api/endpoints';
import type {
  FillLobbyBotsRequest,
  HealthResponse,
  LaunchGameRequest,
  LobbyRequest,
  LobbySettingsPatch,
  LobbyStatusResponse,
  PoolStatusResponse,
  SeriesCreateRequest,
} from '../api/types';
import { normalizeHistory } from '../features/history/normalize';
import { runLobbyOperation, type LobbyOperation } from '../features/lobbies/lobbyOperations';

export { normalizeHistory };

/**
 * Polling intervals live here rather than in components, so the refresh
 * behaviour of the whole app is visible in one place. The old client polled
 * from inside a bespoke store and had to hand-roll deep equality to stop the
 * UI thrashing; the query cache handles that.
 */
export const REFRESH = {
  lobbies: 5_000,
  lobbySummary: 4_000,
  /** Backstop while the lobby long poll is connected: it pushes changes itself. */
  lobbySummaryLive: 20_000,
  pool: 10_000,
  health: 30_000,
  chat: 4_000,
} as const;

export const queryKeys = {
  health: ['health'] as const,
  lobbies: ['lobbies'] as const,
  lobbySummary: (lobbyId: string | number) => ['lobby-summary', String(lobbyId)] as const,
  pool: ['pool'] as const,
  history: (params: Record<string, unknown>) => ['lobby-history', params] as const,
  chat: (botId: string) => ['lobby-chat', botId] as const,
  sentMessages: (botId: string) => ['lobby-sent', botId] as const,
};

type QueryTuning<T> = Partial<Pick<UseQueryOptions<T>, 'enabled' | 'refetchInterval'>>;

export function useHealth(options: QueryTuning<HealthResponse> = {}) {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => health.get(),
    refetchInterval: REFRESH.health,
    // A 503 from /health is a real answer about the system, not a failed
    // request, so surface it immediately instead of retrying into a spinner.
    retry: false,
    ...options,
  });
}

export function useLobbies(options: QueryTuning<LobbyStatusResponse> = {}) {
  return useQuery({
    queryKey: queryKeys.lobbies,
    queryFn: () => lobbies.list(),
    refetchInterval: REFRESH.lobbies,
    ...options,
  });
}

export function useLobbySummary(
  lobbyId: string | number | null | undefined,
  { refetchInterval = REFRESH.lobbySummary }: { refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: queryKeys.lobbySummary(lobbyId ?? 'none'),
    queryFn: () => lobbies.summary(lobbyId as string | number),
    enabled: lobbyId !== null && lobbyId !== undefined && lobbyId !== '',
    refetchInterval,
  });
}

export function usePool(options: QueryTuning<PoolStatusResponse> = {}) {
  return useQuery({
    queryKey: queryKeys.pool,
    queryFn: () => pool.status(),
    refetchInterval: REFRESH.pool,
    ...options,
  });
}

export function useLobbyHistory(params: {
  match_id?: number;
  bot_id?: string;
  last?: number;
  limit?: number;
}) {
  return useQuery({
    queryKey: queryKeys.history(params),
    queryFn: async () => normalizeHistory(await lobbies.history(params)),
  });
}

export function useLobbyChat(botId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.chat(botId ?? 'none'),
    queryFn: () => bot.chatMessages(botId as string),
    enabled: Boolean(botId) && enabled,
    refetchInterval: REFRESH.chat,
  });
}

/** Invalidating both lists is what almost every mutation needs afterwards. */
export function useRefreshLobbyViews() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.lobbies });
    void queryClient.invalidateQueries({ queryKey: queryKeys.pool });
  };
}

export function useCreateLobby() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: (payload: LobbyRequest) => lobbies.create(payload),
    onSuccess: refresh,
  });
}

export function useCreateSeries() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: (payload: SeriesCreateRequest) => lobbies.createSeries(payload),
    onSuccess: refresh,
  });
}

export function useLaunchGame() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: (payload: LaunchGameRequest) => lobbies.launch(payload),
    onSuccess: refresh,
  });
}

export function useFillBots() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: (payload: FillLobbyBotsRequest) => lobbies.fillBots(payload),
    onSuccess: refresh,
  });
}

export function useDestroyLobby() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: (botId: string) => bot.destroyLobby(botId),
    onSuccess: refresh,
  });
}

export function useInvitePlayers() {
  return useMutation({
    mutationFn: ({ botId, steamIds }: { botId: string; steamIds: string[] }) =>
      bot.invite(botId, steamIds),
  });
}

export function useSendLobbyMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ botId, message }: { botId: string; message: string }) =>
      bot.sendMessage(botId, message),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.chat(variables.botId) });
    },
  });
}

export function useChangeLobbySettings() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: ({ botId, settings }: { botId: string; settings: LobbySettingsPatch }) =>
      bot.changeSettings(botId, settings),
    onSuccess: refresh,
  });
}

/**
 * One mutation for every seat / kick / shuffle style action on a live lobby.
 * The Game Coordinator confirms through the lobby cache, so the summary is
 * refetched right away and again on its own poll.
 */
export function useLobbyOperation() {
  const queryClient = useQueryClient();
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: ({ botId, op }: { botId: string; lobbyId: string | number; op: LobbyOperation }) =>
      runLobbyOperation(botId, op),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lobbySummary(variables.lobbyId) });
      refresh();
    },
  });
}

export function useReconnectPool() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: (forceAll: boolean) => pool.reconnect(forceAll),
    onSuccess: refresh,
  });
}

export function usePoolLogout() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: () => pool.logout(),
    onSuccess: refresh,
  });
}

export function useBotAction() {
  const refresh = useRefreshLobbyViews();
  return useMutation({
    mutationFn: ({ botId, action }: { botId: string; action: 'leave' | 'abandon' | 'logout' }) => {
      if (action === 'leave') return bot.leaveLobby(botId);
      if (action === 'abandon') return bot.abandonLobby(botId);
      return bot.logout(botId);
    },
    onSuccess: refresh,
  });
}
